// WebSocket to a conversation's Durable Object. Auto-reconnects with backoff and
// replays the catch-up cursors so no message is missed across a reconnect.
import type { ServerEvent, ClientEvent } from "@shared/wire.js";

export interface Cursor {
  since_seq: number;
  since_tomb: number;
}

export class ConvSocket {
  private ws: WebSocket | null = null;
  private closed = false;
  private backoff = 1000;

  constructor(
    private convId: string,
    private token: string,
    private onEvent: (ev: ServerEvent) => void,
    private cursor: () => Cursor,
    private sharePresence = false,
  ) {}

  connect(): void {
    if (this.closed) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const { since_seq, since_tomb } = this.cursor();
    const presence = this.sharePresence ? "&presence=1" : "";
    const url = `${proto}://${location.host}/ws/conv/${this.convId}?token=${encodeURIComponent(this.token)}&since_seq=${since_seq}&since_tomb=${since_tomb}${presence}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onmessage = (e) => {
      try {
        this.onEvent(JSON.parse(e.data) as ServerEvent);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onopen = () => {
      this.backoff = 1000;
    };
    ws.onclose = () => {
      if (this.closed) return;
      setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, 20000);
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* already closing */
      }
    };
  }

  /** Send an ephemeral event (typing / read / screenshot) if the socket is open. */
  send(msg: ClientEvent): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(msg));
      } catch {
        /* dropped; these are best-effort */
      }
    }
  }

  close(): void {
    this.closed = true;
    try {
      this.ws?.close();
    } catch {
      /* already closing */
    }
  }
}
